<p-toast></p-toast>
<p-dialog
  header="Edit Check-in and Check-out"
  [(visible)]="displayModal"
  [modal]="true"
  [style]="{ width: '30vw' }"
  [contentStyle]="{ 'max-height': '80vh', overflow: 'auto' }"
  (onHide)="closeModal()"
  [draggable]="false"
  [resizable]="true"
  styleClass="edit-checkin-dialog"
  [closable]="true"
>
  <ng-container *ngIf="isLoading">
    <div class="flex justify-center items-center p-8">
      <p-progressSpinner styleClass="w-12 h-12" strokeWidth="4"></p-progressSpinner>
    </div>
  </ng-container>

  <ng-container *ngIf="!isLoading && currentVisitDetails && activityDetails">
    <div class="p-fluid">
      <!-- Appointment Info -->
      <div
        class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 mb-4 flex items-center"
      >
        <i class="pi pi-calendar text-blue-500 dark:text-blue-300 text-2xl mr-4"></i>
        <div>
          <p class="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1 flex items-center">
            {{ 'appointments' | dynamicText | async }} Details
          </p>
          <p class="text-sm text-slate-600 dark:text-slate-300">
            Date:
            <span class="font-medium text-blue-600 dark:text-blue-300">{{
              getFormattedDate(currentVisitDetails.date)
            }}</span>
            | Scheduled Start:
            <span class="font-medium text-blue-600 dark:text-blue-300">{{ getScheduledStartTime() }}</span>
            | Scheduled End:
            <span class="font-medium text-blue-600 dark:text-blue-300">{{ getScheduledEndTime() }}</span>
          </p>
        </div>
      </div>

      <!-- Caregiver Info -->
      <div class="p-2 border border-slate-200 dark:border-slate-700 rounded-lg mb-4 text-slate-700 dark:text-slate-200">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
            <i class="pi pi-user text-xl text-slate-500 dark:text-slate-300"></i>
          </div>
          <div class="flex flex-col md:flex-row md:items-center gap-1">
            <span class="text-sm font-semibold text-slate-800 dark:text-slate-100"
              >Caregiver: {{ caregiverName || 'N/A' }}</span
            >
            <span class="text-xs text-slate-500 dark:text-slate-300 md:ml-2 flex items-center">
              <i class="pi pi-phone text-xs text-slate-400 dark:text-slate-300 mr-1"></i>
              Phone: {{ caregiverPhoneNumber || 'N/A' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Check-In/Out Form -->
      <form [formGroup]="checkInOutForm" class="flex flex-col gap-4">
        <!-- Check-In and Check-Out Time in same row -->
        <!-- Hide the time inputs when the visit is not checked in -->
        <ng-container *ngIf="!checkInOutForm.get('isHospitalized')?.value && activityDetails.isCheckedIn">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Check-In Time -->
            <div class="flex flex-col">
              <label class="font-medium text-slate-700 dark:text-slate-200 mb-1">Check-in Time</label>
              <ng-container *ngIf="activityDetails.isCheckedIn; else notCheckedIn">
                <div class="flex">
                  <div class="flex-1">
                    <app-time-picker
                      formControlName="checkInTime"
                      placeholder="Select check-in time"
                      [showIcon]="true"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                      label="Check-in Time"
                    ></app-time-picker>
                  </div>
                </div>
              </ng-container>
              <ng-template #notCheckedIn>
                <p
                  class="text-slate-500 dark:text-slate-300 mt-2 p-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-800/60"
                >
                  Did not check in yet.
                </p>
              </ng-template>
            </div>

            <!-- Check-Out Time -->
            <div class="flex flex-col">
              <label class="font-medium text-slate-700 dark:text-slate-200 mb-1">Check-out Time</label>
              <ng-container *ngIf="activityDetails.isCheckedOut; else notCheckedOut">
                <div class="flex">
                  <div class="flex-1">
                    <app-time-picker
                      formControlName="checkOutTime"
                      placeholder="Select check-out time"
                      [showIcon]="true"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                      label="Check-out Time"
                    ></app-time-picker>
                  </div>
                </div>
                <small *ngIf="isCheckoutBeforeCheckin" class="p-error mt-1"
                  >Check-out time cannot be before check-in time.</small
                >
              </ng-container>
              <ng-template #notCheckedOut>
                <p
                  class="text-slate-500 dark:text-slate-300 mt-2 p-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-800/60"
                >
                  Did not check out yet.
                </p>
              </ng-template>
            </div>
          </div>
        </ng-container>

        <!-- Visit Time Summary (informational, not inputs) -->
        <ng-container
          *ngIf="
            activityDetails.isCheckedIn && activityDetails.isCheckedOut && !checkInOutForm.get('isHospitalized')?.value
          "
        >
          <div
            class="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
            role="region"
            aria-label="Visit time summary"
          >
            <div class="flex items-center text-blue-600 dark:text-blue-300 mb-2">
              <i class="pi pi-clock mr-2"></i>
              <span class="font-medium">Visit Time Summary</span>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <!-- Total Time chip -->
              <span
                class="px-2 py-1 rounded-md text-sm border flex items-center gap-1"
                [ngClass]="{
                  'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-300 dark:border-red-400':
                    isCheckoutBeforeCheckin || isOverTime,
                  'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-200 border-blue-300 dark:border-blue-400':
                    !isCheckoutBeforeCheckin && !isOverTime
                }"
              >
                <i class="pi pi-stopwatch"></i>
                <span>Total: {{ totalTime || '-' }}</span>
              </span>

              <!-- Max Allowed Time chip -->
              <span
                class="px-2 py-1 rounded-md text-sm border bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 flex items-center gap-1"
              >
                <i class="pi pi-hourglass"></i>
                <span>Max Today: {{ maxTimeDisplay }}</span>
              </span>

              <!-- Context messages -->
              <span
                *ngIf="isOverTime"
                class="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-200 dark:border-yellow-500/40 px-2 py-1 rounded"
              >
                Total time exceeds the maximum allowed.
              </span>
              <span
                *ngIf="isCheckoutBeforeCheckin"
                class="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-400/50 px-2 py-1 rounded"
              >
                Invalid range: check-out is before check-in.
              </span>

              <!-- Accessible live updates for screen readers -->
              <span class="sr-only" aria-live="polite">
                {{
                  'Total ' +
                    (totalTime || '-') +
                    '. Max allowed ' +
                    maxTimeDisplay +
                    (isOverTime ? '. Over limit.' : '')
                }}
              </span>
            </div>
          </div>
        </ng-container>

        <!-- Hospitalization Section -->
        <div
          *ngIf="activityDetails.isCheckedIn && activityDetails.isCheckedOut"
          class="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          <div class="flex items-center text-blue-600 dark:text-blue-300 mb-3">
            <i class="pi pi-building mr-2"></i>
            <span class="font-medium">Hospitalization</span>
          </div>
          <div class="field-checkbox flex items-center mb-3">
            <p-checkbox
              formControlName="isHospitalized"
              [binary]="true"
              inputId="isHospitalized"
              (onChange)="onHospitalizedChange()"
            ></p-checkbox>
            <label for="isHospitalized" class="ml-2">Patient is Hospitalized?</label>
          </div>
          <!-- Hospitalization Type Dropdown - Only visible when hospitalized is checked -->
          <div *ngIf="checkInOutForm.get('isHospitalized')?.value" class="mb-3">
            <label for="hospitalizationType" class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Hospitalization Type
            </label>
            <p-select
              formControlName="hospitalizationTypeSkey"
              [options]="hospitalizationTypes"
              optionLabel="displayName"
              optionValue="sKey"
              placeholder="Select hospitalization type"
              styleClass="w-full"
              [showClear]="true"
              (onChange)="onHospitalizationTypeChange()"
              appendTo="body"
              panelStyleClass="edit-checkin-open-up"
            ></p-select>
          </div>

          <!-- Hospitalization Reason - Only visible when "Other" hospitalization type is selected -->
          <div *ngIf="checkInOutForm.get('isHospitalized')?.value && isOtherHospitalizationTypeSelected()">
            <label
              for="hospitalizationReason"
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
            >
              Hospitalization Reason
            </label>
            <textarea
              pTextarea
              formControlName="hospitalizationReason"
              rows="2"
              placeholder="Add hospitalization reason"
              class="w-full"
            ></textarea>
          </div>
        </div>

        <!-- Task Note Section -->
        <div
          *ngIf="
            !checkInOutForm.get('isHospitalized')?.value && activityDetails.isCheckedIn && activityDetails.isCheckedOut
          "
          class="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          <div class="flex items-center text-blue-600 dark:text-blue-300 mb-3">
            <i class="pi pi-file-edit mr-2"></i>
            <span class="font-medium">Task Note</span>
          </div>
          <textarea pTextarea formControlName="taskNote" rows="3" placeholder="Add task note" class="w-full"></textarea>
        </div>

        <!-- Monthly Tasks Section -->
        <div
          *ngIf="!checkInOutForm.get('isHospitalized')?.value && activityDetails.isCheckedIn && monthlyTasks.length > 0"
          class="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          <div class="flex items-center text-blue-600 dark:text-blue-300 mb-3">
            <i class="pi pi-calendar mr-2"></i>
            <span class="font-medium">Monthly Tasks</span>
          </div>
          <div *ngFor="let task of monthlyTasks" class="field-checkbox flex items-center mb-2">
            <p-checkbox
              [formControlName]="'task_' + task.taskSkey"
              [binary]="true"
              [inputId]="'task_' + task.taskSkey"
              (onChange)="onTaskCheckboxChange(task.taskSkey)"
            ></p-checkbox>
            <label [for]="'task_' + task.taskSkey" class="ml-2">
              {{ task?.patientTask?.displayName || task?.patientTask?.name }}
            </label>
          </div>
        </div>

        <!-- Other Tasks Section -->
        <div
          *ngIf="
            !checkInOutForm.get('isHospitalized')?.value && activityDetails.isCheckedIn && activityDetails.isCheckedOut
          "
          class="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          <div class="flex items-center text-blue-600 dark:text-blue-300 mb-3">
            <i class="pi pi-list mr-2"></i>
            <span class="font-medium">Other Tasks</span>
          </div>
          <ng-container *ngIf="otherTasks.length > 0; else noOtherTasks">
            <div *ngFor="let task of otherTasks" class="field-checkbox flex items-center mb-2">
              <p-checkbox
                [formControlName]="'task_' + task.taskSkey"
                [binary]="true"
                [inputId]="'task_' + task.taskSkey"
                (onChange)="onTaskCheckboxChange(task.taskSkey)"
              ></p-checkbox>
              <label [for]="'task_' + task.taskSkey" class="ml-2">
                {{ task?.patientTask?.displayName || task?.patientTask?.name }}
              </label>
            </div>
          </ng-container>
          <ng-template #noOtherTasks>
            <div class="text-slate-500 dark:text-slate-300 text-sm">No other tasks</div>
          </ng-template>
        </div>
      </form>
    </div>
  </ng-container>

  <ng-template pTemplate="footer">
    <div class="flex flex-row justify-between items-center w-full pt-2">
      <ng-container *ngIf="isOverTime">
        <div class="flex items-center ml-2">
          <i class="pi pi-exclamation-triangle text-red-500 dark:text-red-400 mr-2"></i>
          <span class="text-xs text-red-600 dark:text-red-400"
            >Total time exceeds the maximum allowed. Please adjust the times.</span
          >
        </div>
      </ng-container>
      <div class="flex gap-2">
        <p-button label="Cancel" icon="pi pi-times" (onClick)="closeModal()" styleClass="p-button-text"></p-button>

        <ng-container *ngIf="!isLoading && activityDetails">
          <p-button
            *ngIf="activityDetails.isCheckedIn && activityDetails.isCheckedOut"
            label="Update"
            icon="pi pi-check"
            (onClick)="onUpdateTime()"
            [loading]="isUpdating"
            [disabled]="isUpdating || checkInOutForm.invalid || isCheckoutBeforeCheckin || isActionButtonDisabled"
          ></p-button>

          <p-button
            *ngIf="!activityDetails.isCheckedIn"
            label="Force Clock-In"
            icon="pi pi-sign-in"
            (onClick)="onForceCheckIn()"
            [loading]="isUpdating"
            [disabled]="isUpdating || isActionButtonDisabled"
            styleClass="p-button-warning"
          ></p-button>

          <p-button
            *ngIf="activityDetails.isCheckedIn && !activityDetails.isCheckedOut"
            label="Force Clock-Out"
            icon="pi pi-sign-out"
            (onClick)="onForceCheckOut()"
            [loading]="isUpdating"
            [disabled]="isUpdating || isActionButtonDisabled"
            styleClass="p-button-danger"
          ></p-button>
        </ng-container>
      </div>
    </div>
  </ng-template>
</p-dialog>
